import React, { useEffect, useCallback } from 'react';
import '../home-styling/PackageDetailsModals.css';

// Constants for better maintainability
const DAY_NIGHT_FORMATS = {
  SINGLE_DAY: "Day",
  MULTIPLE_DAYS: "Days",
  SINGLE_NIGHT: "Night", 
  MULTIPLE_NIGHTS: "Nights"
};

const PACKAGE_FIELDS = {
  NAME: ['packageName', 'name'],
  DESCRIPTION: ['shortDescription', 'description'],
  LOCATION: ['primaryLocation', 'location'],
  PEOPLE: ['numPeople', 'people_count'],
  CATEGORY: 'packageCategory',
  PRICE: 'price',
  RATINGS: 'ratings',
  REVIEW_COUNT: 'review_count',
  PICKUP_AVAILABLE: 'pickup_available',
  DROPOFF_AVAILABLE: 'dropoff_available',
  DURATION: 'package_duration'
};

const DAY_FIELDS = {
  NUMBER: ['day_number', 'dayNumber'],
  NIGHT_NUMBER: ['night_number', 'nightNumber'],
  OVERNIGHT_STAY: ['has_overnight_stay', 'hasOvernightStay'],
  NO_ACCOMMODATION: 'no_accommodation',
  START_LOCATION: ['start_location', 'startLocation'],
  START_TIME: ['start_date_time', 'startDateTime'],
  PICKUP_MODE: ['pickup_mode', 'pickupMode'],
  PICKUP_DETAILS: ['pickup_details', 'pickupDetails'],
  MEALS: ['selected_meals', 'selectedMeals'],
  DESCRIPTION: ['day_description', 'dayDescription'],
  ACTIVITIES: 'activities',
  STAY_TYPE: ['stay_type', 'stayType'],
  PROPERTY_NAME: ['property_name', 'propertyName'],
  PROPERTY_ADDRESS: ['property_address', 'propertyAddress'],
  FOODING_OPTION: ['fooding_option', 'foodingOption']
};

const ACTIVITY_FIELDS = {
  NAME: ['activity_name', 'name'],
  DESCRIPTION: ['activity_description', 'description'],
  IMAGE: ['activity_image_url', 'activity_image', 'image_url', 'image']
};

const ACCOMMODATION_FIELDS = {
  IMAGE: ['image_url', 'image', 'image_data', 'url'],
  FILENAME: 'filename'
};

const PackageDetailsModal = React.memo(({ packageData, onClose }) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [onClose]);

  // Utility functions
  const getFieldValue = useCallback((obj, fieldOptions) => {
    if (Array.isArray(fieldOptions)) {
      for (const field of fieldOptions) {
        if (obj[field] !== undefined && obj[field] !== null && obj[field] !== '') {
          return obj[field];
        }
      }
      return '';
    }
    return obj[fieldOptions] || '';
  }, []);

  const formatDayNightText = useCallback((summary) => {
    if (!summary.totalDays || summary.totalDays === 0) return "No days planned";
    
    const daysText = `${summary.totalDays} ${summary.totalDays > 1 ? DAY_NIGHT_FORMATS.MULTIPLE_DAYS : DAY_NIGHT_FORMATS.SINGLE_DAY}`;
    
    if (summary.totalNights && summary.totalNights > 0) {
      const nightsText = `${summary.totalNights} ${summary.totalNights > 1 ? DAY_NIGHT_FORMATS.MULTIPLE_NIGHTS : DAY_NIGHT_FORMATS.SINGLE_NIGHT}`;
      return `${daysText} ${nightsText}`.trim();
    }
    
    return daysText;
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not specified';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  }, []);

  const handleImageError = useCallback((e) => {
    console.error('Failed to load image:', e.target.src);
    e.target.style.display = 'none';
  }, []);

  const capitalizeFirst = useCallback((str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, []);

  if (!packageData) return null;

  const { packageInfo, days, summary } = packageData;
  const dayNightFormat = formatDayNightText(summary);

  // Get package field values
  const packageName = getFieldValue(packageInfo, PACKAGE_FIELDS.NAME) || "Untitled Package";
  const packageDescription = getFieldValue(packageInfo, PACKAGE_FIELDS.DESCRIPTION) || "No description provided";
  const packageLocation = getFieldValue(packageInfo, PACKAGE_FIELDS.LOCATION) || "Not specified";
  const numPeople = getFieldValue(packageInfo, PACKAGE_FIELDS.PEOPLE) || "Not specified";
  const packagePrice = packageInfo[PACKAGE_FIELDS.PRICE] ? `₹${packageInfo[PACKAGE_FIELDS.PRICE]}` : "Not specified";
  const packageCategory = packageInfo[PACKAGE_FIELDS.CATEGORY];
  const ratings = packageInfo[PACKAGE_FIELDS.RATINGS];
  const reviewCount = packageInfo[PACKAGE_FIELDS.REVIEW_COUNT];
  const pickupAvailable = packageInfo[PACKAGE_FIELDS.PICKUP_AVAILABLE];
  const dropoffAvailable = packageInfo[PACKAGE_FIELDS.DROPOFF_AVAILABLE];
  const packageDuration = packageInfo[PACKAGE_FIELDS.DURATION];

  // Logo handling
  const packageLogo = packageInfo.logo_url || packageInfo.logoDataURL;

  return (
    <div className="pkg-details-modal" onClick={onClose}>
      <div className="pkg-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="pkg-modal-header">
          <h2>{packageName}</h2>
          <span className="pkg-close-modal" onClick={onClose}>&times;</span>
        </div>
        
        <div className="pkg-modal-body">
          {/* Package Logo */}
          {packageLogo && packageLogo.length > 0 && (
            <div className="pkg-logo-modal">
              <img 
                src={packageLogo} 
                alt={packageName} 
                onError={handleImageError}
                loading="lazy"
              />
            </div>
          )}
          
          {/* Package Overview */}
          <div className="pkg-overview">
            <h3>Package Overview</h3>
            <div className="pkg-overview-grid">
              <div className="pkg-overview-item pkg-full-width">
                <strong>Description:</strong>
                <p>{packageDescription}</p>
              </div>
              
              <div className="pkg-overview-item">
                <strong>Package Name:</strong>
                <span>{packageName}</span>
              </div>
              
              <div className="pkg-overview-item">
                <strong>Location:</strong>
                <span>{packageLocation}</span>
              </div>
              
              <div className="pkg-overview-item">
                <strong>Number of People:</strong>
                <span>{numPeople}</span>
              </div>
              
              <div className="pkg-overview-item">
                <strong>Price:</strong>
                <span className="pkg-price">{packagePrice}</span>
              </div>
              
              <div className="pkg-overview-item">
                <strong>Duration:</strong>
                <span>{dayNightFormat}</span>
              </div>

              {packageCategory && (
                <div className="pkg-overview-item">
                  <strong>Category:</strong>
                  <span>{packageCategory}</span>
                </div>
              )}

              {ratings && (
                <div className="pkg-overview-item">
                  <strong>Ratings:</strong>
                  <span>{ratings}/5.0</span>
                </div>
              )}

              {reviewCount !== undefined && (
                <div className="pkg-overview-item">
                  <strong>Review Count:</strong>
                  <span>{reviewCount}</span>
                </div>
              )}

              {pickupAvailable && (
                <div className="pkg-overview-item">
                  <strong>Pickup Available:</strong>
                  <span className={pickupAvailable === 'available' ? 'pkg-available' : 'pkg-not-available'}>
                    {pickupAvailable}
                  </span>
                </div>
              )}

              {dropoffAvailable && (
                <div className="pkg-overview-item">
                  <strong>Dropoff Available:</strong>
                  <span className={dropoffAvailable === 'available' ? 'pkg-available' : 'pkg-not-available'}>
                    {dropoffAvailable}
                  </span>
                </div>
              )}

              {packageDuration && (
                <div className="pkg-overview-item">
                  <strong>Package Duration:</strong>
                  <span>{packageDuration}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Days Itinerary */}
          {days.length > 0 ? (
            <div className="pkg-days-container">
              <h3>Package Itinerary ({summary.totalDays} Days {summary.totalNights > 0 ? `${summary.totalNights} Nights` : ""})</h3>
              {days.map((day, index) => {
                // Get day field values
                const dayNumber = getFieldValue(day, DAY_FIELDS.NUMBER) || index + 1;
                const nightNumber = getFieldValue(day, DAY_FIELDS.NIGHT_NUMBER);
                const hasOvernightStay = getFieldValue(day, DAY_FIELDS.OVERNIGHT_STAY) !== undefined 
                  ? getFieldValue(day, DAY_FIELDS.OVERNIGHT_STAY)
                  : !day[DAY_FIELDS.NO_ACCOMMODATION];
                
                const startLocation = getFieldValue(day, DAY_FIELDS.START_LOCATION);
                const startDateTime = getFieldValue(day, DAY_FIELDS.START_TIME);
                const pickupMode = getFieldValue(day, DAY_FIELDS.PICKUP_MODE);
                const pickupDetails = getFieldValue(day, DAY_FIELDS.PICKUP_DETAILS);
                const selectedMeals = getFieldValue(day, DAY_FIELDS.MEALS) || [];
                const dayDescription = getFieldValue(day, DAY_FIELDS.DESCRIPTION);
                const activities = day[DAY_FIELDS.ACTIVITIES] || [];
                const stayType = getFieldValue(day, DAY_FIELDS.STAY_TYPE);
                const propertyName = getFieldValue(day, DAY_FIELDS.PROPERTY_NAME);
                const propertyAddress = getFieldValue(day, DAY_FIELDS.PROPERTY_ADDRESS);
                const foodingOption = getFieldValue(day, DAY_FIELDS.FOODING_OPTION);

                const dayNightHeader = hasOvernightStay && nightNumber
                  ? `Day ${dayNumber}`
                  : `Day ${dayNumber}`;

                return (
                  <div key={index} className={`pkg-day-section ${hasOvernightStay ? 'pkg-overnight-stay' : 'pkg-day-only'}`}>
                    <h4>
                      <span className="pkg-day-number">{dayNumber}</span>
                      {dayNightHeader} - Itinerary Details
                      <span className="pkg-stay-badge">{hasOvernightStay ? 'Overnight Stay' : 'Day Only'}</span>
                    </h4>
                    
                    {/* Start Point & Pickup */}
                    {(startLocation || startDateTime || pickupMode) && (
                      <div className="pkg-detail-section">
                        <h5>Start Point & Pickup</h5>
                        <div className="pkg-detail-grid">
                          {startLocation && (
                            <div className="pkg-detail-item">
                              <label>Location:</label>
                              <span>{startLocation}</span>
                            </div>
                          )}
                          {startDateTime && (
                            <div className="pkg-detail-item">
                              <label>Start Time:</label>
                              <span>{startDateTime}</span>
                            </div>
                          )}
                          {pickupMode && (
                            <div className="pkg-detail-item">
                              <label>Pickup Mode:</label>
                              <span>{pickupMode.replace("-", " ")}</span>
                            </div>
                          )}
                          {pickupMode === "available" && pickupDetails && (
                            <div className="pkg-detail-item pkg-full-width pkg-pickup-available">
                              <label> Pickup Location Details:</label>
                              <span>{pickupDetails}</span>
                            </div>
                          )}
                          {pickupMode === "not-available" && (
                            <div className="pkg-detail-item pkg-full-width pkg-pickup-not-available">
                              <span>ℹ️ Pickup service is not available for this day</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Fooding */}
                    {(foodingOption || selectedMeals.length > 0) && (
                      <div className="pkg-detail-section">
                        <h5>Fooding</h5>
                        <div className="pkg-detail-grid">
                          {foodingOption && (
                            <div className="pkg-detail-item">
                              <label>Food Type:</label>
                              <span>{foodingOption}</span>
                            </div>
                          )}
                          {selectedMeals.length > 0 && (
                            <div className="pkg-detail-item">
                              <label>Meals Included:</label>
                              <span>{selectedMeals.map(meal => capitalizeFirst(meal)).join(", ")}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Day Plan */}
                    {dayDescription && (
                      <div className="pkg-detail-section">
                        <h5>Day Plan</h5>
                        <div className="pkg-description-box">
                          <p>{dayDescription}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Activities */}
                    {activities.length > 0 && (
                      <div className="pkg-detail-section">
                        <h5>Activities ({activities.length})</h5>
                        <div className="pkg-activities-list">
                          {activities.map((activity, activityIndex) => {
                            const activityName = getFieldValue(activity, ACTIVITY_FIELDS.NAME);
                            const activityDescription = getFieldValue(activity, ACTIVITY_FIELDS.DESCRIPTION);
                            const activityImage = getFieldValue(activity, ACTIVITY_FIELDS.IMAGE);

                            return activityName ? (
                              <div key={activityIndex} className="pkg-activity-item">
                                <h6>
                                  <span className="pkg-activity-number">{activityIndex + 1}</span>
                                  Activity {activityIndex + 1}
                                </h6>
                                <div className="pkg-detail-grid">
                                  {activityName && (
                                    <div className="pkg-detail-item">
                                      <label>Activity Name:</label>
                                      <span>{activityName}</span>
                                    </div>
                                  )}
                                  {activityDescription && (
                                    <div className="pkg-detail-item">
                                      <label>Highlights:</label>
                                      <span>{activityDescription}</span>
                                    </div>
                                  )}
                                  {activityImage && (
                                    <div className="pkg-detail-item pkg-full-width">
                                      <label>Activity Image:</label>
                                      <div className="pkg-image-preview">
                                        <img 
                                          src={activityImage} 
                                          alt={activityName || "Activity Image"}
                                          onError={handleImageError}
                                          loading="lazy"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Accommodation */}
                    {hasOvernightStay ? (
                      (stayType || propertyName || propertyAddress) && (
                        <div className="pkg-detail-section">
                          <h5>Accommodation (Night {nightNumber})</h5>
                          <div className="pkg-detail-grid">
                            {stayType && (
                              <div className="pkg-detail-item">
                                <label>Stay Type:</label>
                                <span>{stayType}</span>
                              </div>
                            )}
                            {propertyName && (
                              <div className="pkg-detail-item">
                                <label>Property Name:</label>
                                <span>{propertyName}</span>
                              </div>
                            )}
                            {propertyAddress && (
                              <div className="pkg-detail-item">
                                <label>Address:</label>
                                <span>{propertyAddress}</span>
                              </div>
                            )}
                          </div>
                          {(day.accommodation_images || day.accommodationImages || []).length > 0 && (
                            <div className="pkg-accommodation-images">
                              <div className="pkg-images-grid">
                                {(day.accommodation_images || day.accommodationImages || []).map((image, imgIndex) => {
                                  const imageUrl = getFieldValue(image, ACCOMMODATION_FIELDS.IMAGE);
                                  if (!imageUrl) return null;
                                  return (
                                    <div key={imgIndex} className="pkg-image-item">
                                      <img 
                                        src={imageUrl} 
                                        alt={`Accommodation ${imgIndex + 1}`}
                                        onError={handleImageError}
                                        loading="lazy"
                                      />
                                      {image[ACCOMMODATION_FIELDS.FILENAME] && (
                                        <div className="pkg-image-filename">{image[ACCOMMODATION_FIELDS.FILENAME]}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    ) : (
                      <div className="pkg-detail-section pkg-no-overnight">
                        <h5>ℹ️ No Overnight Stay</h5>
                        <p>This day does not include overnight accommodation.</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pkg-no-days">
              <div className="pkg-no-days-icon">📭</div>
              <h4>No Itinerary Planned</h4>
              <p>This package doesn't have any day plans yet.</p>
            </div>
          )}

          {/* Summary Section */}
          {summary && (
            <div className="pkg-overview pkg-summary-section">
              <h3>Package Summary</h3>
              <div className="pkg-overview-grid">
                <div className="pkg-overview-item">
                  <strong>Total Days:</strong>
                  <span>{summary.totalDays || 0}</span>
                </div>
                <div className="pkg-overview-item">
                  <strong>Total Nights:</strong>
                  <span>{summary.totalNights || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default PackageDetailsModal;